import { Canceler } from 'axios';
import { action, makeObservable, observable } from 'mobx';
import moment from 'moment';
import { defaultLocationReportFilters } from '../../../constants/reprots';
import { reportsStore } from '../../../store/reportsStore';
import { BRANCHES_STATUS, IAreasReportParams, IBranchesReportParams, ILocationReportFilters, ILocationReports } from '../types/salesLocation';
import { allOptionsValue } from 'src/constants/main';
import { USER_ROLES } from 'src/constants/user';
import { appStore } from 'src/mobx/appStore';
import { userStore } from 'src/mobx/userStore';
import client from 'src/services/client';
import wms from 'src/services/wms';
import { downloadFile, getCancelToken, getTranslate, hasRole } from 'src/utils/helpers';
import { localeStore } from '~/mobx/localesStore';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

export class SalesLocationStore {
  constructor() {
    makeObservable(this);
  }

  public get persist() {
    return false;
  }

  cancelReportBuild: {
    [key: string]: null | Canceler;
  } = {
    branches: null,
    areas: null,
  };

  @observable filters: ILocationReportFilters = defaultLocationReportFilters;
  @observable reports: ILocationReports = {
    branches: {
      data: undefined,
      loading: false,
    },
    areas: {
      data: undefined,
      loading: false,
    },
  };

  @action setFilters = (filters: Partial<ILocationReportFilters>) => {
    this.filters = { ...this.filters, ...filters };
  };

  @action fetchBranchesReport = async () => {
    try {
      const params: IBranchesReportParams = {
        ...reportsStore.getReportBuildParams(),
        branch_status: this.filters.branchesStatus.value as BRANCHES_STATUS,
      };

      // Get branches from reportsStore when appStore.isAddonsAvailable.franchise
      if (!appStore.isAddonsAvailable.franchise) {
        if (reportsStore.filters.branches.some((option) => option.value === allOptionsValue) || !reportsStore.filters.branches.length) {
          params.branches = hasRole([USER_ROLES.FRANCHISEE]) ? userStore.user.branches.map((branch) => branch.id) : [];
        } else {
          params.branches = reportsStore.filters.branches.map((option) => option.value as number);
        }
      }

      this.reports.branches.loading = true;
      const branchesReport: { [key: string]: any } = await client.get('v3/reports/sales/branches', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.branches = c)),
      });

      this.reports.branches.data = Object.entries(branchesReport).map(([_, body]) => {
        const name = getTranslate(body.branch.name);
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { branch, ...reportBody } = body;
        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesLocationStore fetchBranchesReport' });
    } finally {
      this.reports.branches.loading = false;
    }
  };

  @action fetchAreasReport = async () => {
    try {
      const params: IAreasReportParams = {
        ...reportsStore.getReportBuildParams(),
        areas: this.filters.areas.some((option) => option.value === allOptionsValue)
          ? []
          : this.filters.areas?.map((option) => option.value as number),
      };

      this.reports.areas.loading = true;
      const areasReport: { [key: string]: any } = await client.get('v3/reports/sales/areas', {
        params,
        cancelToken: getCancelToken((c) => (this.cancelReportBuild.areas = c)),
      });

      this.reports.areas.data = Object.entries(areasReport).map(([_, body]) => {
        const name = getTranslate(body.area.name);
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { area, ...reportBody } = body;
        return { name, body: reportBody };
      });
      reportsStore.selectAllBranchesInFirstInit();
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      logger.error({ error, place: 'SalesLocationStore fetchAreasReport' });
    } finally {
      this.reports.areas.loading = false;
    }
  };

  @action exportBranchesLocationReportToXls = async () => {
    const params: IBranchesReportParams = {
      ...reportsStore.getReportBuildParams(),
      branch_status: this.filters.branchesStatus.value as BRANCHES_STATUS,
    };

    await this.exportLocationReportToXls(params, 'branches', 'Branch sales');
  };

  @action exportLocationReportToXls = async (
    params: IAreasReportParams | IBranchesReportParams,
    reportKey: keyof ILocationReports,
    fileName: string
  ) => {
    try {
      this.reports[reportKey].loading = true;
      const locationReport = await client.get(`v3/reports/sales/${reportKey}`, {
        params: {
          ...params,
          export_xls: 1,
          export_xls_url: window.IS_MOBILE ? 1 : 0,
        },
        cancelToken: getCancelToken((c) => (this.cancelReportBuild[reportKey] = c)),
        responseType: window.IS_MOBILE ? 'text' : 'blob',
      });
      this.reports[reportKey].loading = false;

      if (window.IS_MOBILE) {
        wms.downloadFile(locationReport);
      } else {
        downloadFile(locationReport, `${fileName}, ${moment().format('DD-MM-YYYY')}.xls`);
      }

      toastSuccess({ message: localeStore.t('toast_report_exported') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.reports[reportKey].loading = false;
      logger.error({ error, place: 'SalesLocationStore exportLocationReportToXls' });
    }
  };
}

export const salesLocationStore = new SalesLocationStore();
